import React, { useCallback, useEffect } from 'react'
import { useTheme } from '@material-ui/core/styles'
import { useMediaQuery } from '@material-ui/core'
import { useRouteMatch, useParams } from 'react-router-dom'

import {
  useCreateAttempt,
  useAttemptsV2,
  useAttempt,
} from 'api/Package/Attempts/queries'
import { useReadAndParseDigiIebookPackage } from 'api/Package/Packages/queries'
import useNotification from 'hooks/useNotification'
import parseError from 'helpers/errorHelpers'
import useWindowSize from 'hooks/useWindowSize'
import { userRoleSelector } from 'store/auth/selectors'
import { useSubSchoolSetting } from 'api/School/SubSchoolSettings/queries'
import IndTeacherService from 'api/IndividualTeacher'
import { usePackageActivities } from 'api/Package/Activitites/queries'

import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import useStorage from 'hooks/useStorage'
import { useTranslation } from 'react-i18next'
import { useReaderContext } from './context/reader-context'

const drawerWidth = 300

function bindEvent(element, eventName, eventHandler) {
  if (element.addEventListener) {
    element.addEventListener(eventName, eventHandler, false)
  } else if (element.attachEvent) {
    element.attachEvent('on' + eventName, eventHandler)
  }
}
function unBindEvent(element, eventName, eventHandler) {
  if (element.removeEventListener) {
    element.removeEventListener(eventName, eventHandler)
  } else if (element.detachEvent) {
    element.detachEvent('on' + eventName, eventHandler)
  }
}

export const useDigiIebookState = () => {
  const {
    iRef,
    info: {
      currentActivity,
      availableActivities,
      currentIndex,
      packageId,
      pkg,
    },
    readerMode: { IS_ASSIGNMENT_DIGIIEBOOK, IS_ASSIGNMENT_PREVIEW },
    drawer: { open },
    actions: { setCurrentActivity, setCurrentIndex },
  } = useReaderContext()
  const theme = useTheme()
  const isScore100Ref = React.useRef(false)
  const completedActivityId = React.useRef('')
  const { readMode } = useParams()
  const params = new URLSearchParams(location.search)

  const { t } = useTranslation()

  let matchAttempt = useRouteMatch({
    path: '/reader/attempt/:id',
    strict: true,
    sensitive: true,
  })

  const attemptId = !matchAttempt
    ? params.get('attempt')
    : matchAttempt.params.id

  const IS_ATTEMPT = !!matchAttempt?.isExact && readMode === 'attempt'

  const CAN_SHOW_ATTEMPTS = [
    'School Master',
    'School Manager',
    'Individual Teacher',
    'Admin',
    'Teacher',
  ]

  let userRole = useSelector(userRoleSelector)

  const IS_STUDENT = ['Student', 'Member']

  const attempts = useAttempt(attemptId, {
    queryOptions: {
      enabled:
        (CAN_SHOW_ATTEMPTS.includes(userRole) && !!attemptId) || IS_ATTEMPT,
      staleTime: 1000 * 240,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, 'Error getting Attempt'),
        })
      },
    },
  })

  const readAndParseDigiIebookPackage = useReadAndParseDigiIebookPackage(
    packageId,
    pkg.data.version,
    {
      queryOptions: {
        enabled: pkg.isSuccess,
        onError: (error) => {
          addNotification({
            type: 'error',
            message: parseError(
              error,
              t('assignments.assignmentCreate.step1.errorReadingPackage')
            ),
          })
        },
      },
    }
  )

  const [localCurrentActivity, setLocalCurrentActivity] = React.useState()
  const [isActivity, setIsActivity] = React.useState(false)
  const [hasPostedMessage, setHasPostedMessage] = React.useState(false)
  const [pageChange, setPageChange] = React.useState(false)

  const currentSubSchool = useSelector((state) => state.auth.currentSubSchool)
  const SCHOOL_TYPE_2 = currentSubSchool?.type_id === 2
  const currentSchoolID = SCHOOL_TYPE_2
    ? currentSubSchool?.user_id
    : currentSubSchool?.id
  const currentSchoolYear = useSelector((state) => state.auth.currentSchoolYear)

  const [latestAttempts, setLatestAttempts] = useStorage(
    'latestAttempts',
    [],
    'session'
  )
  const [hasLoaded, setHasLoaded] = React.useState(false)

  const lastAttempt = useAttemptsV2(
    currentSchoolID,
    currentSchoolYear?.id,
    null,
    {
      queryFilters: {
        filters: [
          { by: 'Activity.path', value: currentActivity?.path },
          { by: 'sort_by', value: 'created.desc' },
        ],
      },
      queryOptions: {
        enabled:
          IS_STUDENT.includes(userRole) &&
          !!currentActivity &&
          isActivity &&
          !IS_ATTEMPT,
        onError: (error) => {
          addNotification({
            type: 'error',
            message: parseError(error, 'Error getting Attempt'),
          })
        },
        onSuccess: (data) => {
          let currentActivityAttempts
          let payload

          let message

          if (latestAttempts?.length) {
            currentActivityAttempts = latestAttempts
              .filter((attempt) => attempt.path === currentActivity?.path)
              .sort((a, b) => b.created - a.created)
          }

          if (currentActivityAttempts?.length) {
            payload = {
              action: 'studentAnswers',
              answers: JSON.parse(currentActivityAttempts[0].answers),
              mode: attemptId
                ? 'progress'
                : IS_ASSIGNMENT_DIGIIEBOOK || IS_ASSIGNMENT_PREVIEW
                ? 'assignment'
                : null,
            }

            message = JSON.stringify(payload)
          } else if (data?.items?.length) {
            payload = {
              action: 'studentAnswers',
              answers: JSON.parse(data.items[0].answers),
              mode: attemptId
                ? 'progress'
                : IS_ASSIGNMENT_DIGIIEBOOK || IS_ASSIGNMENT_PREVIEW
                ? 'assignment'
                : null,
            }

            message = JSON.stringify(payload)
          } else {
            payload = {
              action: 'studentAnswers',
              answers: null,
              mode: attemptId
                ? 'progress'
                : IS_ASSIGNMENT_DIGIIEBOOK || IS_ASSIGNMENT_PREVIEW
                ? 'assignment'
                : null,
            }

            message = JSON.stringify(payload)
          }

          if (!iRef?.current) return
          if (message) {
            iRef?.current.contentWindow.postMessage(message, '*')
            setHasLoaded(true)
            setHasPostedMessage(true)
          }
        },
      },
    },
    'single'
  )

  useEffect(() => {
    if (!hasPostedMessage && pageChange) {
      if (attempts.isLoading || lastAttempt.isLoading) return

      if (!IS_STUDENT.includes(userRole) && !attempts.data) {
        if (
          latestAttempts?.some(
            (attempt) => attempt.path === currentActivity?.path
          )
        )
          return

        if (
          !lastAttempt.isSuccess &&
          IS_STUDENT.includes(userRole) &&
          !!currentActivity &&
          isActivity
        )
          return

        const payload = {
          action: 'studentAnswers',
          answers: null,
          mode: attemptId
            ? 'progress'
            : IS_ASSIGNMENT_DIGIIEBOOK || IS_ASSIGNMENT_PREVIEW
            ? 'assignment'
            : null,
        }
        const message = JSON.stringify(payload)

        iRef?.current.contentWindow.postMessage(message, '*')
        setHasPostedMessage(true)
      } else if (
        !IS_STUDENT.includes(userRole) &&
        IS_ATTEMPT &&
        attempts.data
      ) {
        const payload = {
          action: 'studentAnswers',
          answers: JSON.parse(attempts.data.answers),
          mode: attemptId
            ? 'progress'
            : IS_ASSIGNMENT_DIGIIEBOOK || IS_ASSIGNMENT_PREVIEW
            ? 'assignment'
            : null,
        }

        const message = JSON.stringify(payload)
        iRef?.current.contentWindow.postMessage(message, '*')
        setHasPostedMessage(true)
      }
    }
  }, [attempts, latestAttempts, lastAttempt])

  const studentAnswers = useMemo(() => {
    if (IS_ATTEMPT) {
      return attempts?.data?.answers
    } else {
      let currentActivityAttempts

      if (latestAttempts?.length) {
        currentActivityAttempts = latestAttempts
          .filter((attempt) => attempt.path == currentActivity?.path)
          .sort((a, b) => b.created - a.created)
      }
      if (currentActivityAttempts?.length) {
        return currentActivityAttempts[0].answers
      }
      if (lastAttempt?.data?.items?.length) {
        return lastAttempt?.data?.items[0]?.answers
      }
    }
  }, [lastAttempt, latestAttempts, attempts])

  // roles that don't post attempts
  const ROLES_WITHOUT_ATTEMPTS = [
    'School Master',
    'School Manager',
    'Individual Teacher',
    'Admin',
    'Teacher',
    'Editor',
  ]

  // roles than can see answers
  const CAN_SHOW_ANSWERS_ROLES = useMemo(
    () => [
      'School Master',
      'School Manager',
      'Individual Teacher',
      'Admin',
      'Teacher',
      'Member',
      'Editor',
    ],
    []
  )

  const upLg = useMediaQuery(theme.breakpoints.up('xl'))

  const { width: windowWidth } = useWindowSize()

  const [, setRetries] = React.useState(0)

  let allowStudentsSeeShowAnswers = useSubSchoolSetting(11, {
    enabled: !SCHOOL_TYPE_2 && 'Member' !== userRole,
  })

  let showAnswersIndTeacherSetting =
    IndTeacherService.Settings.hooks.useIndTeacherSetting(11, {
      enabled: SCHOOL_TYPE_2,
    })

  const SHOW_ANSWERS_BUTTON = useMemo(() => {
    return CAN_SHOW_ANSWERS_ROLES.includes(userRole) ||
      (!SCHOOL_TYPE_2 && allowStudentsSeeShowAnswers.data?.active)
      ? true
      : SCHOOL_TYPE_2
      ? showAnswersIndTeacherSetting.data?.active
      : false
  }, [
    CAN_SHOW_ANSWERS_ROLES,
    userRole,
    SCHOOL_TYPE_2,
    allowStudentsSeeShowAnswers.data,
    showAnswersIndTeacherSetting.data,
  ])

  const currentActivityRef = React.useRef()

  useEffect(() => {
    currentActivityRef.current = currentActivity
  }, [currentActivity])

  useEffect(() => {
    setCurrentActivity(localCurrentActivity)
  }, [localCurrentActivity])
  const canPostAttemptRef = React.useRef(true)

  const { addNotification } = useNotification()

  const [, forceUpdate] = React.useState(0)

  const digiiebooksActivities = usePackageActivities(packageId, null, {
    queryFilters: {
      filters: [{ by: 'path', value: currentActivity?.path }],
    },
    queryOptions: {
      enabled: pkg.isSuccess && !!currentActivity?.path,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(
            error,
            t('assignments.assignmentCreate.step1.errorParsingActivities')
          ),
        })
      },
    },
  })

  const createAttempt = useCreateAttempt({
    queryOptions: {
      onSuccess: async (data) => {
        if (data.elecs) {
          addNotification({
            type: 'success',
            message: `You have earned ${data.elecs} ELECs`,
          })
        } else {
          addNotification({
            type: 'success',
            message: `Successfully created Attempt`,
          })
        }
        setLatestAttempts((prev = []) => {
          const newItem = { ...data, path: currentActivity.path }

          const filtered = prev.filter(
            (item) => item?.path !== currentActivity.path
          )
          return [...filtered, newItem]
        })
        forceUpdate((n) => n + 1)
      },
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, `Error creating Attempt`),
        })
      },
    },
  })

  let calculatedMargin = React.useMemo(() => {
    if ((upLg ? 1920 : windowWidth) - (open ? drawerWidth : 0) > 1060) {
      return `${
        ((upLg ? 1920 : windowWidth) - (open ? drawerWidth : 0) - 1060) / 2
      }px`
    } else return '0px'
  }, [upLg, windowWidth, open])

  // const skillTypeIds = [
  //   { Miscellaneous: 999 },
  //   { Vocabulary: 1000 },
  //   { Listening: 1001 },
  //   { Writing: 1002 },
  //   { Reading: 1003 },
  //   { Speaking: 1004 },
  //   { Grammar: 1005 },
  //   { Communication: 1006 },
  //   { 'Use Of English': 1007 },
  //   { 'Knowledge of Language Means': 1008 },
  //   { 'Knowledge of Language Functions': 1009 },
  //   { Dictation: 1010 },
  //   { 'Knowledge of Language Response': 1011 },
  //   { 'Video Comprehension': 1012 },
  // ]

  const handleCheckScore = (score, answers) => {
    // const toc = readAndParseDigiIebookPackage.data?.toc
    // const tocItem = toc
    //   ? Object.values(toc).find((item) => item.href === currentActivity.path)
    //   : null

    // const skillTypeObj = skillTypeIds.find(
    //   (obj) => Object.keys(obj)[0] === tocItem?.skillType
    // )
    // const skillTypeId = Object.values(skillTypeObj)[0]

    createAttempt.mutate({
      score: score,
      answers: answers,
      activity_id: digiiebooksActivities?.data?.items[0]?.id,
      // Activity: {
      //   title: tocItem?.activityName
      //     ? tocItem?.activityName
      //     : currentActivity.activity,
      //   path: currentActivity.path,
      //   skill_type_id: skillTypeId,
      // },
      // Module: {
      //   title: tocItem?.moduleName
      //     ? tocItem.moduleName
      //     : currentActivity.module,
      //   package_id: packageId,
      // },
    })
  }

  const handleMessage = useCallback(
    (obj) => {
      let message
      let answers

      if (typeof obj.data === 'string' && obj.data !== 'recaptcha-setup') {
        message = JSON.parse(obj.data)

        if (message && message.action === 'checkscore') {
          if (ROLES_WITHOUT_ATTEMPTS.includes(userRole)) return

          answers = JSON.stringify(message.answers)
          if (
            completedActivityId.current !==
            currentActivityRef.current?.activityId
          ) {
            isScore100Ref.current = false
          }
          handleCheckScore(message.answers.scorePercent, answers)
          canPostAttemptRef.current = false
        } else if (message && message.action === 'pagechange') {
          let { action, ...rest } = message
          const newActivity = { ...rest }
          setLocalCurrentActivity(newActivity)
          if (newActivity.activity) {
            setIsActivity(true)
          }
          setPageChange(true)
          setHasPostedMessage(false)
        }
      }
    },
    [ROLES_WITHOUT_ATTEMPTS, userRole, setCurrentActivity]
  )

  React.useEffect(() => {
    bindEvent(window, 'message', handleMessage)
    return () => {
      unBindEvent(window, 'message', handleMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleMessage])

  const showStudentAnswers = useCallback(() => {
    if (!hasPostedMessage) {
      const payload = {
        action: 'studentAnswers',
        answers: JSON.parse(studentAnswers),
        mode: attemptId
          ? 'progress'
          : IS_ASSIGNMENT_DIGIIEBOOK || IS_ASSIGNMENT_PREVIEW
          ? 'assignment'
          : null,
      }

      let message = JSON.stringify(payload)
      if (!iRef?.current) return
      if (!lastAttempt.isSuccess && !IS_ATTEMPT) return

      iRef?.current.contentWindow.postMessage(message, '*')
      setHasPostedMessage(true)

      setHasLoaded(true)
    }
  }, [iRef, studentAnswers, hasPostedMessage])

  useEffect(() => {
    if (studentAnswers && hasLoaded && iRef?.current) {
      showStudentAnswers()
    }
  }, [showStudentAnswers, hasLoaded, studentAnswers, iRef])

  const resetVariables = useCallback(() => {
    setHasLoaded(false)
    setRetries(0)
    canPostAttemptRef.current = true
  }, [setHasLoaded, setRetries])

  const handleClickExercise = useCallback(
    (activity) => {
      if (!currentActivity) return
      if (activity.activityId === currentActivity.activityId) return
      setCurrentActivity(activity)

      let index = availableActivities.findIndex(
        (avail) => avail.activityId === activity.activityId
      )
      setCurrentIndex(index)
      resetVariables()
    },
    [
      availableActivities,
      currentActivity,
      resetVariables,
      setCurrentActivity,
      setCurrentIndex,
    ]
  )

  const handleNext = useCallback(() => {
    if (currentIndex < availableActivities?.length) {
      if (availableActivities[currentIndex + 1]) {
        setCurrentActivity(availableActivities[currentIndex + 1])
      }
      setCurrentIndex(currentIndex + 1)
    }
    resetVariables()
  }, [
    availableActivities,
    currentIndex,
    resetVariables,
    setCurrentActivity,
    setCurrentIndex,
  ])

  const handlePrev = useCallback(() => {
    if (currentIndex !== 0) {
      if (availableActivities[currentIndex - 1]) {
        setCurrentActivity(availableActivities[currentIndex - 1])
      }
      setCurrentIndex(currentIndex - 1)
    }
    resetVariables()
  }, [
    availableActivities,
    currentIndex,
    resetVariables,
    setCurrentActivity,
    setCurrentIndex,
  ])

  const handleLoad = useCallback(() => {
    if (studentAnswers) {
      showStudentAnswers()
    }
    setHasLoaded(true)
  }, [showStudentAnswers, studentAnswers])

  const hasNext = React.useMemo(
    () =>
      availableActivities?.length - 1 !== currentIndex &&
      availableActivities?.length > 0,
    [availableActivities?.length, currentIndex]
  )

  const hasPrev = React.useMemo(() => currentIndex !== 0, [currentIndex])

  return {
    bindEvent,
    unBindEvent,
    iRef,
    ui: {
      upLg,
      SHOW_ANSWERS_BUTTON,
      open,
      hasNext,
      hasPrev,
    },
    actions: {
      handleLoad,
      handleNext,
      handlePrev,
      handleClickExercise,
      showStudentAnswers,
    },
    calculatedMargin,
    hasLoaded,
  }
}
